import React from 'react';
import PropTypes from 'prop-types';
import { Text } from 'office-ui-fabric-react/lib/Text';
import { FocusZone, FocusZoneDirection } from 'office-ui-fabric-react/lib/FocusZone';
import { List } from 'office-ui-fabric-react/lib/List';
import { Persona, PersonaSize } from 'office-ui-fabric-react/lib/Persona';
import differenceBy from 'lodash/differenceBy';
import styles from './DimensionListActivity.style';
import DimensionListActivityDetails from '../DimensionListActivityDetails';
import { can } from '../../utils/auth';
import { dlTypeNames } from '../../constants';

const operationMessages = {
  create: 'created',
  edited: 'edited',
  'submit-for-approval': 'submitted',
  approve: 'approved',
  reject: 'rejected',
  discard: 'discarded',
};

const addedOrRemovedText = (oldData, newData, addOrRemoveStatus, itemsCount) => {
  const difference = differenceBy(newData.listItems, oldData.listItems, 'id');
  const listItems = difference.filter(val => val.isList);
  const itemList = difference.filter(val => !val.isList);
  let listMessage = '';
  let itemMessage = '';
  let changedItems = '';

  if (itemsCount > 4) {
    listMessage =
      listItems.length > 0
        ? `${listItems.length} ${
            listItems.length > 1
              ? `${dlTypeNames[newData.listType]?.plural} lists`
              : `${dlTypeNames[newData.listType]?.singular} list`
          }`
        : '';

    itemMessage =
      itemList.length > 0
        ? `${itemList.length} ${
            itemList.length > 1
              ? `${dlTypeNames[newData.listType]?.plural}`
              : `${dlTypeNames[newData.listType]?.singular}`
          }`
        : '';

    changedItems =
      addOrRemoveStatus === 'added'
        ? `Added ${listMessage} ${
            listMessage !== '' && itemMessage !== '' ? 'and' : ''
          } ${itemMessage}`
        : `Removed ${listMessage} ${
            listMessage !== '' && itemMessage !== '' ? 'and' : ''
          } ${itemMessage}`;
    return (
      <ul className={styles.details}>
        <Text as="li" variant="small" block>
          {changedItems}
        </Text>
      </ul>
    );
  }
  listMessage = listItems?.map(item => {
    return (
      <ul className={styles.details}>
        <Text as="li" variant="small" block>
          {`${addOrRemoveStatus === 'added' ? `Added` : `Removed`} ${
            dlTypeNames[newData.listType]?.singular
          } list #${item.id}`}
        </Text>
      </ul>
    );
  });

  itemMessage = itemList?.map(item => {
    return (
      <ul className={styles.details}>
        <Text as="li" variant="small" block>
          {`${addOrRemoveStatus === 'added' ? `Added` : `Removed`} ${
            dlTypeNames[newData.listType]?.singular
          } #${item.id}`}
        </Text>
      </ul>
    );
  });
  changedItems = [...listMessage, ...itemMessage];
  return changedItems;
};

const editedMessage = (oldData, newData) => {
  if (
    oldData.listName !== newData.listName &&
    oldData.listDescription !== newData.listDescription
  ) {
    return `Edited ${newData.listType} name and description`;
  }
  if (oldData.listName !== newData.listName) {
    return `Edited ${newData.listType} name`;
  }
  if (oldData.listDescription !== newData.listDescription)
    return `Edited ${newData.listType} description`;
  return '';
};

const DimensionListAction = item => {
  const { initiatedBy, newData, itemsAddedCount, itemsDeletedCount, operation } = item.action;
  const { oldData } = item.action;
  const actionMessage = operationMessages[operation] || `${operation}`;
  const initiatedName = can('viewActivityInitiatedBy')
    ? initiatedBy?.name || initiatedBy
    : 'Microsoft';

  return (
    <>
      <Text as="h4" className={styles.itemName}>
        {initiatedName} <span className={styles.actionMessage}>{actionMessage} </span>
        {newData.listName}{' '}
        <span className={styles.actionMessage}>
          {actionMessage === 'submitted' ? 'for review' : ''}{' '}
          {actionMessage === 'discarded' ? 'edits' : ''}{' '}
        </span>
      </Text>
      {operation === 'submit-for-approval' && (
        <ul className={styles.details}>
          <Text as="li" variant="small" block>
            Reviewer Justification: {newData.reviewerJustification}
          </Text>
        </ul>
      )}
      {operation === 'reject' && (
        <ul className={styles.details}>
          <Text as="li" variant="small" block>
            Response: {newData.reviewerJustification}
          </Text>
        </ul>
      )}
      {itemsAddedCount > 0 ? addedOrRemovedText(oldData, newData, 'added', itemsAddedCount) : <></>}
      {itemsDeletedCount > 0 ? (
        addedOrRemovedText(newData, oldData, 'removed', itemsDeletedCount)
      ) : (
        <></>
      )}
      {operation === 'edited' && editedMessage(newData, oldData) !== '' ? (
        <ul className={styles.details}>
          <Text as="li" variant="small" block>
            {editedMessage(newData, oldData)}
          </Text>
        </ul>
      ) : (
        <></>
      )}
    </>
  );
};
const ActivityItem = item => {
  return (
    <div className={styles.activityItem} data-is-focusable>
      <Persona
        imageUrl={can('viewActivityInitiatedBy') ? '' : '/images/msft-avatar.png'}
        size={PersonaSize.size28}
        className={styles.personaCoin}
        hidePersonaDetails
      />

      <div className={styles.itemContent}>
        <DimensionListAction action={item} />
        {item.newData?.name && item.area !== 'contracts' && (
          <Text as="p" variant="small" block className={styles.itemIndex}>
            {item.newData.name}
          </Text>
        )}

        <DimensionListActivityDetails item={item} className={styles.details} />
      </div>
      <Text as="p" variant="small" className={styles.date}>
        {item.timestamp && new Date(item.timestamp).toDateString().split(' ').slice(1).join(' ')}
      </Text>
    </div>
  );
};

const DimensionListActivity = ({ items }) => (
  <FocusZone direction={FocusZoneDirection.vertical}>
    <List items={items} onRenderCell={ActivityItem} />
  </FocusZone>
);

DimensionListActivity.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      timestamp: PropTypes.string.isRequired,
      _id: PropTypes.string.isRequired,
      initiatedBy: PropTypes.shape({
        name: PropTypes.string.isRequired,
        email: PropTypes.string.isRequired,
      }),
      operation: PropTypes.string.isRequired,
      area: PropTypes.string.isRequired,
      newData: PropTypes.shape({
        address: PropTypes.string,
        name: PropTypes.string,
        rate: PropTypes.number,
        calculation: PropTypes.string,
        effectiveDate: PropTypes.string,
        terminationDate: PropTypes.string,
        platform: PropTypes.string,
        contractId: PropTypes.string,
      }),
      oldData: PropTypes.shape({
        name: PropTypes.string,
        rate: PropTypes.number,
        calculation: PropTypes.string,
        effectiveDate: PropTypes.string,
        terminationDate: PropTypes.string,
        platform: PropTypes.string,
      }),
      params: PropTypes.shape({
        contractAddress: PropTypes.string,
      }),
    }),
  ),
};

DimensionListActivity.defaultProps = {
  items: [],
};

export default DimensionListActivity;
